import { type ReactNode } from 'react';
import {
  Navigate,
  RouterProvider,
  createBrowserRouter,
} from 'react-router-dom';

import { appW2aRoutes } from './routes/(w2w)';

export const router = createBrowserRouter([
  {
    errorElement: import.meta.env.DEV ? undefined : <Navigate to={'/'} />,
    path: '/activate',
    lazy: async () => {
      const { GetStarted } = await import('./routes/GetStarted/GetStarted');
      return { Component: GetStarted };
    },
  },
  {
    errorElement: import.meta.env.DEV ? undefined : <Navigate to={'/'} />,
    lazy: async () => {
      const { AppLayout } = await import('./layout/AppLayout/AppLayout');
      return { Component: AppLayout };
    },
    children: [
      {
        lazy: async () => {
          const { ChatLayout } = await import('./layout/ChatLayout/ChatLayout');
          return { Component: ChatLayout };
        },
        children: [
          {
            path: '/chat',
            lazy: async () => {
              const { ChatRoute } = await import('./routes/Chat/Chat');
              return { Component: ChatRoute };
            },
          },
          {
            path: '/chats/:id',
            lazy: async () => {
              const { ChatRoute } = await import('./routes/Chat/Chat');
              return { Component: ChatRoute };
            },
          },
          {
            path: '/share/:shareId',
            lazy: async () => {
              const { ShareRoute } = await import('./routes/Share/Share');
              return { Component: ShareRoute };
            },
          },
          {
            path: '/history',
            lazy: async () => {
              const { HistoryRoute } = await import('./routes/History/History');
              return { Component: HistoryRoute };
            },
          },
          {
            path: '/saved-people',
            lazy: async () => {
              const { SavedPeopleRoute } =
                await import('./routes/SavedPeople/SavedPeople');
              return { Component: SavedPeopleRoute };
            },
          },
        ],
      },
      {
        path: '/checkout',
        lazy: async () => {
          const { CheckoutRoute } = await import('./routes/Checkout/Checkout');
          return { Component: CheckoutRoute };
        },
      },

      {
        lazy: async () => {
          const { AccountLayout } =
            await import('./layout/AccountLayout/AccountLayout');
          return { Component: AccountLayout };
        },
        children: [
          {
            path: '/profile',
            lazy: async () => {
              const { ProfileRoute } =
                await import('./routes/Account/Profile/Profile');
              return { Component: ProfileRoute };
            },
          },
          {
            path: '/change-password',
            lazy: async () => {
              const { ChangePasswordRoute } =
                await import('./routes/Account/ChangePassword/ChangePassword');
              return { Component: ChangePasswordRoute };
            },
          },
          {
            path: '/login-connection',
            lazy: async () => {
              const { LoginConnectionRoute } =
                await import('./routes/Account/LoginConnection/LoginConnection');
              return { Component: LoginConnectionRoute };
            },
          },
          {
            path: '/plans',
            lazy: async () => {
              const { PlansRoute } =
                await import('./routes/Account/Plans/Plans');
              return { Component: PlansRoute };
            },
          },
          {
            path: '/billing-details',
            lazy: async () => {
              const { BillingDetailsRoute } =
                await import('./routes/Account/BillingDetails/BillingDetails');
              return { Component: BillingDetailsRoute };
            },
          },
          {
            path: '/invoices',
            lazy: async () => {
              const { InvoicesRoute } =
                await import('./routes/Account/Invoices/Invoices');
              return { Component: InvoicesRoute };
            },
          },
          {
            path: '/digital-library',
            lazy: async () => {
              const { DigitalLibraryRoute } =
                await import('./routes/Account/DigitalLibrary/DigitalLibrary');
              return { Component: DigitalLibraryRoute };
            },
          },
          {
            path: '/danger-zone',
            lazy: async () => {
              const { DangerZoneRoute } =
                await import('./routes/Account/DangerZone/DangerZone');
              return { Component: DangerZoneRoute };
            },
          },
          {
            path: '/privacy-settings',
            lazy: async () => {
              const { PrivacySettingsRoute } =
                await import('./routes/Account/PrivacySettings/PrivacySettings');
              return { Component: PrivacySettingsRoute };
            },
          },
        ],
      },
      {
        lazy: async () => {
          const { MarketingLayout } =
            await import('./layout/MarketingLayout/MarketingLayout');
          return { Component: MarketingLayout };
        },
        children: appW2aRoutes.protectedRoutes,
      },
      {
        lazy: async () => {
          const { ContentLayout } =
            await import('./layout/ContentLayout/ContentLayout');
          return { Component: ContentLayout };
        },
        children: [
          {
            path: '/pricing',
            lazy: async () => {
              const { PricingRoute } = await import('./routes/Pricing/Pricing');
              return { Component: PricingRoute };
            },
          },
          {
            path: '/contact',
            lazy: async () => {
              const { ContactRoute } = await import('./routes/Contact/Contact');
              return { Component: ContactRoute };
            },
          },
          {
            path: '/help-center',
            lazy: async () => {
              const { HelpCenterRoute } =
                await import('./routes/HelpCenter/HelpCenter');
              return { Component: HelpCenterRoute };
            },
          },
          {
            path: '/terms',
            lazy: async () => {
              const { TermsRoute } =
                await import('./routes/TermsRoute/TermsRoute');
              return { Component: TermsRoute };
            },
          },
          {
            path: '/privacy',
            lazy: async () => {
              const { PrivacyRoute } =
                await import('./routes/PrivacyRoute/PrivacyRoute');
              return { Component: PrivacyRoute };
            },
          },
          {
            path: '/refund-policy',
            lazy: async () => {
              const { RefundPolicyRoute } =
                await import('./routes/RefundPolicyRoute/RefundPolicyRoute');
              return { Component: RefundPolicyRoute };
            },
          },
        ],
      },
    ],
  },
  {
    errorElement: import.meta.env.DEV ? undefined : <Navigate to={'/'} />,
    lazy: async () => {
      const { AuthLayout } = await import('./layout/AuthLayout/AuthLayout');
      return { Component: AuthLayout };
    },
    children: [
      {
        path: '/login',
        lazy: async () => {
          const { LoginRoute } = await import('./routes/Login/Login');
          return { Component: LoginRoute };
        },
      },
      {
        path: '/register',
        lazy: async () => {
          const { RegisterRoute } = await import('./routes/Register/Register');
          return { Component: RegisterRoute };
        },
      },
      {
        path: '/forgot-password',
        lazy: async () => {
          const { ForgotPasswordRoute } =
            await import('./routes/ForgotPassword/ForgotPassword');
          return { Component: ForgotPasswordRoute };
        },
      },
      {
        path: '/auth/action',
        lazy: async () => {
          const { ActionHandlerRoute } =
            await import('./routes/ActionHandler/ActionHandler');
          return { Component: ActionHandlerRoute };
        },
      },
      {
        lazy: async () => {
          const { MarketingLayout } =
            await import('./layout/MarketingLayout/MarketingLayout');
          return { Component: MarketingLayout };
        },
        children: appW2aRoutes.routes,
      },
    ],
  },
]);

export function AppRouter(): ReactNode {
  return <RouterProvider router={router} />;
}
